import axios from "axios";

// Configuração base da API
// Em produção, evita fallback para localhost (quebra no navegador do cliente).
// Se VITE_API_URL não for informado, usa rota relativa no mesmo domínio.
const API_BASE_URL = import.meta.env.VITE_API_URL?.trim() || "/api";

// Criar instância do axios
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Interceptor para adicionar token de autenticação e empresa ativa
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Injetar o header da empresa ativa (multi-tenancy) quando houver seleção.
    const empresaAtivaId = localStorage.getItem("empresaAtivaId");
    if (empresaAtivaId && !config.headers["X-Empresa-Id"]) {
      config.headers["X-Empresa-Id"] = empresaAtivaId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Interceptor para tratar respostas e erros
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const requestUrl = error.config?.url || "";
    const isLoginRequest = requestUrl.includes("/auth/login");

    // Se token expirado ou inválido (fora do fluxo de login), redirecionar para login
    if (status === 401 && !isLoginRequest) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }

    // Erros de multi-tenancy: mensagens amigáveis orientando o usuário.
    const errorCode = error.response?.data?.code;
    let errorMessage =
      error.response?.data?.error || error.message || "Erro desconhecido";

    if (errorCode === "SEM_EMPRESA") {
      errorMessage =
        "Você ainda não pertence a nenhuma empresa. Crie ou entre em uma empresa em \"Empresas\" para continuar.";
    } else if (errorCode === "EMPRESA_NAO_SELECIONADA") {
      errorMessage =
        "Nenhuma empresa selecionada. Escolha a empresa ativa no seletor da barra superior.";
    }

    return Promise.reject({
      ...error,
      message: errorMessage,
      code: errorCode,
      status,
    });
  },
);

// Serviços de Autenticação
export const authService = {
  login: (credentials) => api.post("/auth/login", credentials),
  logout: () => api.post("/auth/logout"),
  refresh: (token) => api.post("/auth/refresh", { token }),
  getProfile: () => api.get("/auth/profile"),
  updateProfile: (data) => api.put("/auth/profile", data),
  changePassword: (data) => api.post("/auth/change-password", data),
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append("avatar", file);
    return api.post("/auth/profile/avatar", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  deleteAvatar: () => api.delete("/auth/profile/avatar"),
};

// Serviços de Clientes
export const clienteService = {
  getAll: (params = {}) => api.get("/clientes", { params }),
  getById: (id) => api.get(`/clientes/${id}`),
  create: (data) => api.post("/clientes", data),
  update: (id, data) => api.put(`/clientes/${id}`, data),
  delete: (id) => api.delete(`/clientes/${id}`),
  search: (nome) => api.get("/clientes/search", { params: { nome } }),
  searchByDocument: (documento) => api.get(`/clientes/document/${documento}`),
  getCidades: () => api.get("/clientes/cidades"),
  getStats: () => api.get("/clientes/stats"),
  toggleStatus: (id) => api.patch(`/clientes/${id}/toggle-status`),
};

// Serviços de Usuários
export const usuarioService = {
  getAll: (params = {}) => api.get("/usuarios", { params }),
  getById: (id) => api.get(`/usuarios/${id}`),
  create: (data) => api.post("/usuarios", data),
  update: (id, data) => api.put(`/usuarios/${id}`, data),
  delete: (id) => api.delete(`/usuarios/${id}`),
  toggleStatus: (id) => api.patch(`/usuarios/${id}/toggle-status`),
  getStats: () => api.get("/usuarios/stats"),
};

// Serviços de Empresas (multi-tenancy)
export const empresaService = {
  getAll: () => api.get("/empresas"),
  getById: (id) => api.get(`/empresas/${id}`),
  create: (data) => api.post("/empresas", data),
  update: (id, data) => api.put(`/empresas/${id}`, data),
  delete: (id) => api.delete(`/empresas/${id}`),

  // Membros da empresa
  getMembros: (id) => api.get(`/empresas/${id}/membros`),
  addMembro: (id, data) => api.post(`/empresas/${id}/membros`, data),
  updateMembro: (id, userId, data) =>
    api.put(`/empresas/${id}/membros/${userId}`, data),
  removeMembro: (id, userId) =>
    api.delete(`/empresas/${id}/membros/${userId}`),

  // Logo da empresa (multipart; o GET exige Bearer, por isso vem como blob).
  uploadLogo: (id, file) => {
    const formData = new FormData();
    formData.append("logo", file);
    return api.post(`/empresas/${id}/logo`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  getLogo: (id) => api.get(`/empresas/${id}/logo`, { responseType: "blob" }),
};

// Serviços de Produtos
export const produtoService = {
  getAll: (params = {}, config = {}) =>
    api.get("/produtos", { params, ...config }),
  getById: (id) => api.get(`/produtos/${id}`),
  create: (data) => api.post("/produtos", data),
  update: (id, data) => api.put(`/produtos/${id}`, data),
  delete: (id) => api.delete(`/produtos/${id}`),
  search: (nome) => api.get("/produtos/search", { params: { valor: nome } }),
  getByGroup: (grupoId) => api.get(`/produtos/grupo/${grupoId}`),
  getLowStock: () => api.get("/produtos/estoque-baixo"),
  getStats: () => api.get("/produtos/stats"),
  updateStock: (id, data) => api.post(`/produtos/${id}/estoque`, data),
  // Imagem do produto (multipart; o GET exige Bearer, por isso vem como blob).
  uploadImagem: (id, file) => {
    const formData = new FormData();
    formData.append("imagem", file);
    return api.post(`/produtos/${id}/imagem`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  deleteImagem: (id) => api.delete(`/produtos/${id}/imagem`),
  getImagem: (id, versao) =>
    api.get(`/produtos/${id}/imagem`, {
      responseType: "blob",
      params: versao ? { v: versao } : undefined,
    }),
  // Entrada e quebra agora ficam apenas em `estoqueService` (`/estoque/*`),
  // que escreve nas tabelas reais com `empresa_id`.
};

// Serviços de Grupos de Produtos
export const grupoProdutoService = {
  getAll: (params = {}, config = {}) =>
    api.get("/grupos-produto", { params, ...config }),
  getById: (id) => api.get(`/grupos-produto/${id}`),
  create: (data) => api.post("/grupos-produto", data),
  update: (id, data) => api.put(`/grupos-produto/${id}`, data),
  delete: (id) => api.delete(`/grupos-produto/${id}`),
  search: (nome) =>
    api.get("/grupos-produto/search", { params: { valor: nome } }),
  getAllWithProductCount: () => api.get("/grupos-produto/with-count"),
  getProducts: (id) => api.get(`/grupos-produto/${id}/produtos`),
  getStats: () => api.get("/grupos-produto/stats"),
};

// Serviços de Pedidos
export const pedidoService = {
  getAll: (params = {}, config = {}) =>
    api.get("/pedidos", { params, ...config }),
  getById: (id) => api.get(`/pedidos/${id}`),
  create: (data) => api.post("/pedidos", data),
  update: (id, data) => api.put(`/pedidos/${id}`, data),
  delete: (id) => api.delete(`/pedidos/${id}`),
  search: (params) => api.get("/pedidos/search", { params }),
  getByClient: (clienteId) => api.get(`/pedidos/cliente/${clienteId}`),
  getByDateRange: (params) => api.get("/pedidos/por-data", { params }),
  getStats: () => api.get("/pedidos/stats"),
  updateStatus: (id, status) => api.patch(`/pedidos/${id}/status`, { status }),

  // Itens do pedido
  getItems: (id) => api.get(`/pedidos/${id}/itens`),
  addItem: (id, data) => api.post(`/pedidos/${id}/itens`, data),
  updateItem: (id, itemId, data) =>
    api.put(`/pedidos/${id}/itens/${itemId}`, data),
  removeItem: (id, itemId) => api.delete(`/pedidos/${id}/itens/${itemId}`),

  // Ações especiais
  baixarPedido: (id) => api.post(`/pedidos/${id}/baixar`),
  recuperarPedido: (id) => api.post(`/pedidos/${id}/recuperar`),
  cancelarPedido: (id) => api.post(`/pedidos/${id}/cancelar`),
  imprimirPedido: (id, config = {}) =>
    api.get(`/pedidos/${id}/imprimir`, config),
};

// Serviços de Orçamentos: ver `services/orcamentoAdapter.js` (módulo #30).

// Serviços de Estoque
export const estoqueService = {
  getResumoEstoque: () => api.get("/estoque"),
  getMovimentacoes: (params = {}) =>
    api.get("/estoque/movimentacoes", { params }),
  getEntradas: (params = {}) => api.get("/estoque/entradas", { params }),
  getQuebras: (params = {}) => api.get("/estoque/quebras", { params }),
  getEstoqueProduto: (produtoId) => api.get(`/estoque/produto/${produtoId}`),
  getHistoricoProduto: (produtoId, params = {}) =>
    api.get(`/estoque/produto/${produtoId}/historico`, { params }),
  getProdutosBaixoEstoque: () => api.get("/estoque/produtos-baixo"),

  // Movimentações
  registrarEntrada: (data) => api.post("/estoque/entrada", data),
  registrarQuebra: (data) => api.post("/estoque/quebra", data),
  ajustarEstoque: (data) => api.post("/estoque/ajuste", data),
  transferirEstoque: (data) => api.post("/estoque/transferencia", data),

  // Relatórios
  getRelatorioGeral: (params = {}) =>
    api.get("/estoque/relatorio/geral", { params }),
  getRelatorioMovimentacao: (params = {}) =>
    api.get("/estoque/relatorio/movimentacao", { params }),
  getRelatorioQuebras: (params = {}) =>
    api.get("/estoque/relatorio/quebras", { params }),
  getRelatorioPorPeriodo: (params = {}) =>
    api.get("/estoque/relatorio/por-periodo", { params }),

  // Estoque temporário
  getEstoqueTemp: () => api.get("/estoque/temp"),
  montarEstoqueTemp: (data) => api.post("/estoque/temp/montar", data),
  limparEstoqueTemp: () => api.delete("/estoque/temp/limpar"),
};

// Serviços de Relatórios
export const relatorioService = {
  // Clientes
  getRelatorioClientes: (params = {}, config = {}) =>
    api.get("/relatorios/clientes", { params, ...config }),
  getClientesPorCidade: (params = {}) =>
    api.get("/relatorios/clientes/por-cidade", { params }),
  getClientesAniversariantes: (params = {}) =>
    api.get("/relatorios/clientes/aniversariantes", { params }),
  getClientesInativos: (params = {}) =>
    api.get("/relatorios/clientes/inativos", { params }),

  // Produtos
  getRelatorioProdutos: (params = {}, config = {}) =>
    api.get("/relatorios/produtos", { params, ...config }),
  getProdutosEstoqueBaixo: (params = {}) =>
    api.get("/relatorios/produtos/estoque-baixo", { params }),
  getProdutosSemEstoque: (params = {}) =>
    api.get("/relatorios/produtos/sem-estoque", { params }),
  getProdutosPorGrupo: (params = {}) =>
    api.get("/relatorios/produtos/por-grupo", { params }),
  getProdutosMaisVendidos: (params = {}) =>
    api.get("/relatorios/produtos/mais-vendidos", { params }),

  // Pedidos
  getRelatorioPedidos: (params = {}, config = {}) =>
    api.get("/relatorios/pedidos", { params, ...config }),
  getPedidosPorCliente: (params = {}) =>
    api.get("/relatorios/pedidos/por-cliente", { params }),
  getPedidosPorPeriodo: (params = {}) =>
    api.get("/relatorios/pedidos/por-periodo", { params }),
  getPedidosAbertos: (params = {}) =>
    api.get("/relatorios/pedidos/abertos", { params }),
  getPedidosFinalizados: (params = {}) =>
    api.get("/relatorios/pedidos/finalizados", { params }),
  getPedidosCancelados: (params = {}) =>
    api.get("/relatorios/pedidos/cancelados", { params }),
  getFaturamentoPedidos: (params = {}) =>
    api.get("/relatorios/pedidos/faturamento", { params }),

  // Orçamentos
  getRelatorioOrcamentos: (params = {}, config = {}) =>
    api.get("/relatorios/orcamentos", { params, ...config }),
  getOrcamentosPendentes: (params = {}) =>
    api.get("/relatorios/orcamentos/pendentes", { params }),
  getOrcamentosConvertidos: (params = {}) =>
    api.get("/relatorios/orcamentos/convertidos", { params }),
  getOrcamentosPorCliente: (params = {}, config = {}) =>
    api.get("/relatorios/orcamentos/por-cliente", { params, ...config }),

  // Dashboard
  getDashboardData: () => api.get("/relatorios/dashboard"),
  getEstatisticasGerais: (params = {}, config = {}) =>
    api.get("/relatorios/estatisticas/gerais", { params, ...config }),
  getEstatisticasVendas: (params = {}) =>
    api.get("/relatorios/estatisticas/vendas", { params }),
  getEstatisticasClientes: (params = {}) =>
    api.get("/relatorios/estatisticas/clientes", { params }),
  getEstatisticasProdutos: (params = {}) =>
    api.get("/relatorios/estatisticas/produtos", { params }),

  // Personalizados
  getRelatorioPersonalizado: (data) =>
    api.post("/relatorios/personalizado", data),
  getTemplatesRelatorio: () => api.get("/relatorios/templates"),
};

// Utilitários
export const formatError = (error) => {
  // Erros de multi-tenancy têm prioridade: o interceptor já gerou a mensagem
  // orientativa em error.message a partir do código retornado pela API.
  const code = error?.code || error?.response?.data?.code;
  if (
    error?.message &&
    (code === "SEM_EMPRESA" || code === "EMPRESA_NAO_SELECIONADA")
  ) {
    return error.message;
  }
  if (error.response?.data?.error) {
    return error.response.data.error;
  }
  if (error.message) {
    return error.message;
  }
  return "Erro desconhecido";
};

export const isTokenValid = () => {
  const token = localStorage.getItem("token");
  if (!token) return false;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

export default api;
